<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { apiGet } from '@/stores/api'
import { 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-vue-next'
import CalendarWeekGrid from '@/components/calendar/CalendarWeekGrid.vue'
import logger from '@/utils/logger'

const authStore = useAuthStore()

const show = ref(false)
const loading = ref(false)
interface TechUser {
  id: number
  username: string
  first_name?: string
  last_name?: string
}
interface CalendarEvent {
  id: number
  tech_id: number
  start_time: string
  end_time: string
  linked_type: 'ticket' | 'project_task' | 'todo' | 'outlook_external'
  linked_id: number
  linked_title: string
  notes: string
  source?: 'hq' | 'outlook'
}
const techs = ref<TechUser[]>([])
const allEvents = ref<CalendarEvent[]>([])
const weekStart = ref(getMonday(new Date()))

let hideTimeout: ReturnType<typeof setTimeout> | null = null

const isStaff = computed(() => authStore.isStaff)

const weekLabel = computed(() => {
  const start = weekStart.value
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
})

function getMonday(d: Date): Date {
  const date = new Date(d)
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1)
  date.setDate(diff)
  date.setHours(0, 0, 0, 0)
  return date
}

function handleMouseEnter() {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
  show.value = true
  if (allEvents.value.length === 0) {
    fetchData()
  }
}

function handleMouseLeave() {
  hideTimeout = setTimeout(() => {
    show.value = false
  }, 150)
}

async function fetchData() {
  loading.value = true
  try {
    const response = await apiGet<{ techs?: TechUser[]; events?: Record<string, CalendarEvent[]> }>('/scheduled-events/resource/week', {
      week_start: weekStart.value.toISOString()
    })
    techs.value = response.techs || []

    // Flatten all events from all techs
    const eventsMap = response.events || {}
    allEvents.value = Object.values(eventsMap).flat()
  } catch (error) {
    logger.error('Failed to fetch calendar data:', error)
  } finally {
    loading.value = false
  }
}

function prevWeek(e: Event) {
  e.stopPropagation()
  const d = new Date(weekStart.value)
  d.setDate(d.getDate() - 7)
  weekStart.value = d
  fetchData()
}

function nextWeek(e: Event) {
  e.stopPropagation()
  const d = new Date(weekStart.value)
  d.setDate(d.getDate() + 7)
  weekStart.value = d
  fetchData()
}

function goToToday(e: Event) {
  e.stopPropagation()
  weekStart.value = getMonday(new Date())
  fetchData()
}

onMounted(() => {
  // Pre-fetch on mount if staff
  if (isStaff.value) {
    fetchData()
  }
})

function getTechDisplayName(tech: TechUser): string {
  if (tech.first_name && tech.last_name) {
    return `${tech.first_name} ${tech.last_name}`
  }
  if (tech.first_name) {
    return tech.first_name
  }
  return tech.username.split('@')[0]
}
</script>

<template>
  <div 
    v-if="isStaff"
    class="relative"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Trigger Button -->
    <button 
      class="p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors"
      title="Quick Calendar View"
    >
      <Calendar class="w-5 h-5" />
    </button>

    <!-- Dropdown Panel -->
    <Transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div 
        v-if="show"
        class="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-[60px] sm:top-full sm:mt-2 w-auto sm:w-[500px] bg-surface-800 border border-surface-700 rounded-xl shadow-2xl z-50"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-3 border-b border-surface-700">
          <span class="text-sm font-medium text-surface-100">Team Schedule</span>
          
          <div class="flex items-center gap-1">
            <button 
              @click="prevWeek" 
              class="p-1 hover:bg-surface-700 rounded transition-colors"
            >
              <ChevronLeft class="w-4 h-4" />
            </button>
            <button 
              @click="goToToday"
              class="px-2 py-0.5 text-xs text-surface-400 hover:text-surface-100 hover:bg-surface-700 rounded"
            >
              Today
            </button>
            <span class="text-xs text-surface-400 min-w-[110px] text-center">{{ weekLabel }}</span>
            <button 
              @click="nextWeek" 
              class="p-1 hover:bg-surface-700 rounded transition-colors"
            >
              <ChevronRight class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="p-3">
          <div v-if="loading" class="flex items-center justify-center py-8">
            <Loader2 class="w-5 h-5 animate-spin text-primary-400" />
          </div>
          
          <div v-else-if="techs.length === 0" class="text-center py-8 text-surface-500 text-sm">
            No technicians found
          </div>

          <div v-else class="space-y-3 max-h-[400px] overflow-y-auto">
            <!-- Show each tech's schedule -->
            <div v-for="tech in techs" :key="tech.id">
              <CalendarWeekGrid
                :events="allEvents.filter(e => e.tech_id === tech.id)"
                :week-start="weekStart"
                :compact="true"
                :show-tech-name="true"
                :tech-name="getTechDisplayName(tech)"
              />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-3 pb-3">
          <router-link 
            to="/calendar" 
            class="block w-full text-center text-xs text-primary-400 hover:text-primary-300 py-2 hover:bg-surface-700/50 rounded transition-colors"
          >
            View Full Calendar →
          </router-link>
        </div>
      </div>
    </Transition>
  </div>
</template>
